import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FixedSizeList } from 'react-window'

import {
  Box,
  Button,
  Spinner,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { usePrompt } from 'modules/core/lib/react-router'

import { useCreateEmployeesInBulk } from 'modules/employees/hooks/useCreateEmployeesInBulk'
import { useEmployeeImporterFields } from 'modules/employees/hooks/useEmployeeImporterFields'
import useGetDateFormat from 'modules/employees/hooks/useGetDateFormat'
import useLegalEntities from 'modules/employees/hooks/useLegalEntities'
import { WizardData } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/WizardData'
import { useLocations } from 'modules/locations/hooks/useLocations'

import CheckoutModal from './components/CheckoutModal'
import ErrorsSidepanel from './components/ErrorsSidepanel'
import Filters, { type FilterStatuses } from './components/Filters'
import MapValuesModal from './components/MapValuesModal'
import RowsWrapper from './components/RowsWrapper'
import TableWrapper from './components/TableWrapper'
import useResizeVirtualizeTable from './components/useResizeVirtualizedTable'
import { TableContext } from './context'
import { type Row } from './editableContentReducer'
import useEditableContent from './useEditableContent'
import useTableFilters from './useTableFilters'

const ROW_HEIGHT = 65

const isCompletedStep = () => true

const EditionStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const tableHeadRef = useRef<HTMLTableSectionElement>(null)
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const [sidebarOpened, setOpenSidebar] = useState(false)
  const [focusedCell, setFocusedCell] = useState({ rowIndex: 0, columnIndex: 0 })
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false)
  const [unrecognizedEnumName, setUnrecognizedEnumName] = useState<string>('')
  const [allSelected, setAllSelected] = useState(true)

  const { availableFormats } = useGetDateFormat()

  const { loading: loadingLegalEntities, mainLegalEntity } = useLegalEntities()
  const { locations, loading: loadingLocations } = useLocations()
  const { loading: loadingFields, fields } = useEmployeeImporterFields()
  const { createEmployeesInBulk } = useCreateEmployeesInBulk()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const loading = loadingFields || loadingLegalEntities || loadingLocations
  const orderedMappedFields = data.mappedFields?.sort((a, b) => {
    const columnIndexA = a.columnIndex || 0
    const columnIndexB = b.columnIndex || 0
    return columnIndexA - columnIndexB
  })
  const mainLocation = locations.find((location) => location.main === true)

  const { tableBodyRect } = useResizeVirtualizeTable({
    tableHeadRef,
    tableBodyRef,
    tableContainerRef,
    loading,
  })

  useOnNext(() => {
    track(trackingEvents.importEmployees.editionStep.next)
    setCheckoutModalOpen(true)
  })

  const {
    setInitialState,
    editCell,
    mapValues,
    stepData,
    rowsFilteredByMappedFields,
    toggleSelectRow,
    selectAllRows,
  } = useEditableContent({
    onDataChanged,
    wizardData: data,
  })

  const { rowsWithError, cellCoordinatesWithWarnings, rowsWithWarnings } = stepData

  const hasRowsWithWarnings = rowsWithWarnings && rowsWithWarnings?.length > 0
  const hasRowsWithErrors = rowsWithError && rowsWithError.length > 0

  const { rowsFiltered, setRowFilter, rowFilterStatus, filtersCount } = useTableFilters({
    stepData,
    rows: rowsFilteredByMappedFields,
  })

  const rowsWithoutErrors = new Set(
    rowsFilteredByMappedFields?.filter(
      (_, index) => !rowsWithError?.includes(index) && !rowsWithWarnings?.includes(index)
    )
  )

  const ignoredRows: number[] =
    stepData.rows?.reduce((acc: number[], row: Row | undefined) => {
      if (!row || row.selected || !row.index) return acc
      return [...acc, row.index]
    }, []) || []

  const handleOnSubmit = async () => {
    await createEmployeesInBulk({
      wizardData: data,
      rowsWithError: rowsWithError,
      ignoredRows: ignoredRows,
      cellCoordinatesWithWarnings: cellCoordinatesWithWarnings,
    })

    navigate(navigation.employees.list.fullPath())
  }

  const onChangeRowFilter = (value: FilterStatuses) => {
    setRowFilter(value)
  }

  const headerFilteredByMappedFields = data.editedFileContent?.headers?.map((_, index) => {
    const isHeaderMapped = data.mappedFields?.some(
      (mappedField) => mappedField.columnIndex === index
    )

    if (isHeaderMapped) return data.editedFileContent?.headers?.[index]

    return undefined
  })

  const onCellClicked = (rowIndex: number, columnIndex: number) => {
    setFocusedCell({ rowIndex, columnIndex })
  }

  const enumOptions = (fieldName: string) => {
    switch (fieldName) {
      case 'legal_entity_id':
      case 'location_id':
      case 'team_id':
      case 'job_catalog_level_id':
      case 'nationality':
      case 'country':
        return (
          fieldByName(fieldName)?.allowedValues?.map(({ key: id, value: name }) => ({
            name,
            id,
          })) || []
        )

      default:
        return [{ name: '', id: '' }]
    }
  }

  const calloutDescription = (fieldName: string) => {
    switch (fieldName) {
      case 'legal_entity_id':
        return i18n.t('importer_process.edition_step.map_modal.callout.description_with_default', {
          value: mainLegalEntity?.legalName,
        })
      case 'location_id':
        return i18n.t('importer_process.edition_step.map_modal.callout.description_with_default', {
          value: mainLocation?.name,
        })
      case 'team_id':
        return i18n.t(
          'importer_process.edition_step.map_modal.callout.description_without_default',
          {
            type: fieldByName('team_id')?.label,
          }
        )
      default:
        return undefined
    }
  }

  const fieldByName = (fieldName: string) => {
    return fields?.find((field) => field.fieldName === fieldName)
  }

  const unrecognizedValues = (fieldName: string) => {
    const field = fieldByName(fieldName)
    if (!field) return []

    const columnIndex =
      data.mappedFields?.find(({ fieldName }) => fieldName === field.fieldName)?.columnIndex || 0

    const unrecognizedCellsCoordinates = stepData?.cellCoordinatesWithWarnings?.filter(
      (cell) => cell.columnIndex === columnIndex
    )
    const values = rowsFilteredByMappedFields
      ?.filter((_, i) => unrecognizedCellsCoordinates?.some((cell) => cell.rowIndex === i))
      .map((row) =>
        row?.cells
          ?.filter((cell) => cell?.columnIndex === columnIndex)
          .map((cell) => cell?.value || '')
      )
      .flat()

    return [...new Set(values)].filter(Boolean)
  }

  const handleOnCellChange = (
    rowIndex: number,
    columnIndex: number,
    value: string,
    isValid: boolean
  ) => {
    editCell(rowIndex, columnIndex, value, isValid)
  }

  const handleCheckboxToggle = () => {
    selectAllRows(!allSelected)
    setAllSelected(!allSelected)
  }

  usePrompt(i18n.t('common.confirm_navigation'))

  useUnsafeEffect(() => {
    if (!fields || loading) return
    if (!!stepData.rows?.length) return

    setInitialState({
      editedFileContent: data.editedFileContent,
      fields,
      mappedFields: data.mappedFields,
      legalEntities: enumOptions('legal_entity_id'),
      locations: enumOptions('location_id'),
      teams: enumOptions('team_id'),
      availableRoleLevels: enumOptions('job_catalog_level_id'),
      availableNationalities: enumOptions('nationality'),
      availableCountries: enumOptions('country'),
      dateFormats: availableFormats,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, loading])

  if (loading)
    return (
      <Box flexGrow justifyContent="center" alignItems="center" paddingY="s24">
        <Spinner />
      </Box>
    )

  return (
    <Box flexDirection="column" gap="s24" paddingRight="s8" height="full" overflowX="hidden">
      <Box flexDirection="column" gap="s8">
        <Text.Header2>{i18n.t('importer_process.edition_step.header')}</Text.Header2>
        <Text.Normal>{i18n.t('importer_process.edition_step.header_description')}</Text.Normal>
      </Box>
      {!stepData?.cellCoordinatesWithError?.length &&
        !stepData?.cellCoordinatesWithWarnings?.length && (
          <Box gap="s4">
            <Callout
              description={i18n.tp('importer_process.edition_step.callout.success.description', {
                count: rowsWithoutErrors.size,
              })}
              type="positive"
              compact
            />
          </Box>
        )}
      {(hasRowsWithErrors || hasRowsWithWarnings) && (
        <Box gap="s8">
          {hasRowsWithErrors && (
            <Callout
              type="critical"
              description={i18n.tp('importer_process.edition_step.callout.criticals.title', {
                count: rowsWithError?.length,
              })}
              compact
              link={
                <Button onClick={() => setOpenSidebar(true)} type="secondary">
                  {i18n.t('importer_process.edition_step.callout.criticals.link')}
                </Button>
              }
            />
          )}
          {hasRowsWithWarnings && (
            <Callout
              type="warning"
              description={i18n.tp('importer_process.edition_step.callout.warnings.new_title', {
                count: rowsWithWarnings?.length,
              })}
              compact
              link={
                <Button onClick={() => setOpenSidebar(true)} type="secondary">
                  {i18n.t('importer_process.edition_step.callout.warnings.link')}
                </Button>
              }
            />
          )}
        </Box>
      )}
      <Box alignItems="flexStart">
        <Filters
          onChangeFilter={onChangeRowFilter}
          filterStatus={rowFilterStatus}
          count={filtersCount}
        />
      </Box>
      <Box
        paddingBottom="s24"
        ref={tableContainerRef}
        height="full"
        overflowX="auto"
        position="relative"
      >
        <TableContext.Provider
          value={{
            rows: rowsFiltered?.filter(Boolean),
            mappings: orderedMappedFields,
            headers: headerFilteredByMappedFields,
            rowsWithError: rowsWithError,
            enumOptions,
            onCellChange: handleOnCellChange,
            onCellClicked: onCellClicked,
            toggleSelectRow: toggleSelectRow,
            allSelected: allSelected,
            handleCheckboxToggle: handleCheckboxToggle,
            focusedCell: focusedCell,
            loading: loading,
            fields: fields,
            tableHeadRef,
            tableBodyRef,
            onClearFilter: onChangeRowFilter,
          }}
        >
          <Box position="absolute">
            <FixedSizeList
              height={tableBodyRect.height}
              width={tableBodyRect.width}
              itemCount={rowsFiltered?.filter(Boolean).length || 0}
              itemSize={ROW_HEIGHT}
              innerElementType={TableWrapper}
            >
              {RowsWrapper}
            </FixedSizeList>
          </Box>
        </TableContext.Provider>
      </Box>
      {sidebarOpened && (
        <ErrorsSidepanel
          fields={fields || []}
          onClose={() => {
            setOpenSidebar(false)
          }}
          stepData={stepData}
          setUnrecognizedEnumName={setUnrecognizedEnumName}
          setRowFilter={setRowFilter}
        />
      )}
      {!!unrecognizedEnumName && (
        <MapValuesModal
          unrecognizedOptions={unrecognizedValues(unrecognizedEnumName)}
          translatedName={fieldByName(unrecognizedEnumName)?.label || ''}
          validOptions={enumOptions(unrecognizedEnumName)}
          onClose={setUnrecognizedEnumName.bind(null, '')}
          onSave={(mappedValues) => mapValues(mappedValues, unrecognizedEnumName)}
          calloutDescription={calloutDescription(unrecognizedEnumName)}
        />
      )}
      {!!checkoutModalOpen && (
        <CheckoutModal
          rows={stepData.rows}
          rowsWithError={rowsWithError || []}
          rowsWithWarning={rowsWithWarnings || []}
          onClose={() => setCheckoutModalOpen(false)}
          onSubmit={handleOnSubmit}
          onReview={() => {
            setOpenSidebar(true)
          }}
        />
      )}
    </Box>
  )
}

export const editionStep = (): WizardStep<WizardData> => ({
  title: i18n.t('importer_process.edition_step.title'),
  nextText: i18n.t('importer_process.edition_step.next'),
  render: (props) => <EditionStep {...props} />,
  isCompleted: () => isCompletedStep(),
})
